// src/utils/roleUtils.js

/**
 * Parse a full role claim into a simplified category
 * @param {string} claim - The full role claim (e.g., "applicant-TPC_Group-TechPerformanceCentre")
 * @returns {string} - The simplified role category
 */
export const parseRole = (claim = "") => {
  if (claim.startsWith("Hero-")) return "staff";
  if (/^jobapplicant/i.test(claim)) return "jobApp";
  if (/^opportunityuser/i.test(claim)) return "opportunityUser";
  if (/^applicant/i.test(claim)) return "applicant";
  if (/^learner/i.test(claim)) return "learner";
  if (/^bursarylearner/i.test(claim)) return "bursaryLearner";
  if (/^freetrial/i.test(claim)) return "freetrial";
  if (claim === "vaimo-client-proposal") return "vip";
  return "guest";
};

export const getRoleClaims = (claims = {}) =>
  [
    claims?.role,
    ...(Array.isArray(claims?.roles) ? claims.roles : []),
  ]
    .map((role) => String(role || "").trim())
    .filter(Boolean);

export const hasDashboardClaim = (claimsOrRoles, dashboard) => {
  const roles = Array.isArray(claimsOrRoles)
    ? claimsOrRoles
    : getRoleClaims(claimsOrRoles);
  const expectedPrefix = {
    learner: "learner-",
    hero: "hero-",
    facilitator: "facilitator-",
  }[String(dashboard || "").toLowerCase()];
  if (!expectedPrefix) return false;
  return roles.some((role) =>
    String(role || "").toLowerCase().startsWith(expectedPrefix),
  );
};

/**
 * Check if a user role matches any of the allowed roles
 * @param {string} userRole - The user's role
 * @param {string[]} allowedRoles - Array of allowed roles
 * @returns {boolean} - True if user has an allowed role
 */
export const hasAllowedRole = (userRole, allowedRoles) => {
  if (!userRole || !allowedRoles || allowedRoles.length === 0) {
    return true;
  }

  const userRoles = Array.isArray(userRole) ? userRole : [userRole];
  const result = allowedRoles.some((allowedRole) => userRoles.some((role) => {
    // Exact match
    if (role === allowedRole) return true;

    // Prefix match (e.g., "applicant" matches "applicant-TPC_Group-TechPerformanceCentre")
    if (role && role.startsWith(allowedRole + "-")) return true;
    return false;
  }));

  return result;
};

/**
 * Get the appropriate dashboard route for a user role
 * @param {string} userRole - The user's role
 * @returns {string} - The dashboard route
 */
export const getDashboardRoute = (userRole) => {
  const roleCategory = parseRole(userRole);

  switch (roleCategory) {
    case "staff":
      return "/hero-dashboard";
    case "applicant":
    case "learner":
    case "freetrial":
    case "bursaryLearner":
      return "/learner-dashboard";
    case "vip":
      return "/vaimo-proposal-dashboard";
    case "opportunityUser":
      return "/opportunity-page";
    default:
      return "/unauthorized";
  }
};

/**
 * Check if a user state is allowed for learner dashboard access
 * @param {string} userState - The user's state from the database
 * @returns {boolean} - True if user state is allowed
 */
export const isAllowedUserState = (userState) => {
  const ALLOWED_STATES = ["Applicant", "Learner", "ProfileComplete"];
  return userState && ALLOWED_STATES.includes(userState);
};
